/* gf-search — the locale- and market-aware Finch Finder.
   Shopify's localized predictive-search JSON response is the source of truth
   for resource names, resource URLs, market availability, and prices. */
(function () {
  'use strict';

  /* Pure helpers are declared before DOM setup so the offline runtime test can
     exercise URL, response, and currency behavior without a browser fixture. */
  function predictiveJsonUrl(baseUrl, query, limit, origin) {
    var url = new URL(baseUrl, origin);
    url.pathname = url.pathname.replace(/\/$/, '');
    if (!/\.json$/i.test(url.pathname)) url.pathname += '.json';
    url.search = '';
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection');
    url.searchParams.set('resources[limit]', String(limit));
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,tag');
    return url.toString();
  }

  function resultSets(payload) {
    var resources = payload && (payload.resources || (payload.predictive_search && payload.predictive_search.resources));
    var results = resources && (resources.results || resources);
    return {
      products: results && Array.isArray(results.products) ? results.products : [],
      collections: results && Array.isArray(results.collections) ? results.collections : []
    };
  }

  function decimalPrice(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string' || !/^-?\d+(?:\.\d+)?$/.test(value.trim())) return null;
    var amount = Number(value);
    return Number.isFinite(amount) ? amount : null;
  }

  function formatMoney(value, locale, currency) {
    var amount = decimalPrice(value);
    if (amount === null) return '';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'symbol'
      }).format(amount);
    } catch (error) {
      return currency + '\u00a0' + amount.toFixed(2);
    }
  }

  function sameOriginStorefrontUrl(value, origin, rootValue) {
    if (!value) return '';
    try {
      var url = new URL(value, origin);
      if (url.origin !== new URL(origin).origin) return '';
      var rootPath = new URL(rootValue || '/', origin).pathname.replace(/\/?$/, '/');
      if (
        rootPath !== '/' &&
        /^\/(?:products|collections)(?:\/|$)/.test(url.pathname)
      ) {
        url.pathname = rootPath + url.pathname.replace(/^\//, '');
      }
      return url.pathname + url.search + url.hash;
    } catch (error) {
      return '';
    }
  }

  function quickChoice(value) {
    if (typeof value === 'string') {
      value = value.trim();
      return value ? { label: value, query: value } : null;
    }
    if (!value || typeof value !== 'object') return null;
    var label = typeof value.label === 'string' ? value.label.trim() : '';
    var query = typeof value.query === 'string' ? value.query.trim() : '';
    if (!label) label = query;
    if (!query) query = label;
    return label && query ? { label: label, query: query } : null;
  }

  if (typeof window.__GF_FIND_TEST_HOOK__ === 'function') {
    window.__GF_FIND_TEST_HOOK__({
      predictiveJsonUrl: predictiveJsonUrl,
      resultSets: resultSets,
      formatMoney: formatMoney,
      sameOriginStorefrontUrl: sameOriginStorefrontUrl,
      quickChoice: quickChoice
    });
  }

  var root = document.getElementById('gf-find');
  if (!root || root.__wired) return;

  var configNode = root.querySelector('[data-gf-find-config]');
  var config;
  try {
    config = JSON.parse(configNode ? configNode.textContent : '');
  } catch (error) {
    return;
  }
  if (!config || !config.predictiveSearchUrl || !config.searchUrl || !config.strings) return;

  root.__wired = true;
  var strings = config.strings;
  var input = root.querySelector('.gf-find__input');
  var body = root.querySelector('.gf-find__body');
  var form = root.querySelector('[data-gf-find-form]');
  var footRight = root.querySelector('[data-gf-find-count]');
  var selected = 0;
  var rows = [];
  var currentQuery = '';
  var previousFocus = null;
  var debounceTimer = null;
  var placeholderTimer = null;
  var placeholderIndex = 0;
  var requestController = null;
  var requestSerial = 0;
  var responseCache = Object.create(null);
  var responseCacheKeys = [];

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  function replaceToken(template, key, value) {
    return String(template || '').split('[' + key + ']').join(String(value));
  }

  function cacheKey(query) {
    try {
      return query.toLocaleLowerCase(config.locale || undefined);
    } catch (error) {
      return query.toLowerCase();
    }
  }

  function cacheResponse(key, value) {
    if (!Object.prototype.hasOwnProperty.call(responseCache, key)) responseCacheKeys.push(key);
    responseCache[key] = value;
    if (responseCacheKeys.length > 20) delete responseCache[responseCacheKeys.shift()];
  }

  function imageUrl(product) {
    var featured = product && product.featured_image;
    var value = typeof featured === 'string'
      ? featured
      : featured && (featured.url || featured.src);
    if (!value && product) value = product.image;
    if (!value) return '';
    try {
      var url = new URL(value, location.origin);
      if (!/^https?:$/.test(url.protocol)) return '';
      url.searchParams.set('width', '112');
      return url.toString();
    } catch (error) {
      return '';
    }
  }

  function regexEscape(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function highlightedTitle(title, query) {
    var terms = String(query || '').trim().split(/\s+/).filter(function (term) {
      return term.length > 1;
    });
    if (!terms.length) return esc(title);
    var pattern;
    try {
      pattern = new RegExp('(' + terms.map(regexEscape).join('|') + ')', 'giu');
    } catch (error) {
      return esc(title);
    }
    return String(title).split(pattern).map(function (part, index) {
      return index % 2 ? '<mark>' + esc(part) + '</mark>' : esc(part);
    }).join('');
  }

  function productPrice(product) {
    if (!product) return '';
    var value = product.price_min != null ? product.price_min : product.price;
    if (typeof value === 'string' && !/^-?\d+(?:\.\d+)?$/.test(value.trim())) {
      return value;
    }
    return formatMoney(value, config.locale, config.currency);
  }

  function productRowHtml(product, index, query) {
    var url = sameOriginStorefrontUrl(product.url, location.origin, config.rootUrl);
    var title = product.title || '';
    if (!url || !title) return '';
    var image = imageUrl(product);
    var price = productPrice(product);
    return '<a class="gf-find__row" role="option" id="gf-find-opt-' + index + '" aria-selected="' + (index === 0 ? 'true' : 'false') + '" href="' + esc(url) + '" data-result-type="product">' +
      '<span class="gf-find__thumb">' + (image
        ? '<img src="' + esc(image) + '" alt="" loading="lazy" decoding="async" width="56" height="56">'
        : '<span aria-hidden="true">✦</span>') + '</span>' +
      '<span class="gf-find__info">' +
        '<span class="gf-find__name">' + highlightedTitle(title, query) + '</span>' +
        '<span class="gf-find__meta"><span class="gf-find__type">' + esc(strings.product) + '</span></span>' +
      '</span>' +
      (price ? '<span class="gf-find__right"><span class="gf-find__price">' + esc(price) + '</span></span>' : '') +
      '<svg class="gf-find__go" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>' +
    '</a>';
  }

  function preferencesAllowed() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    return !!(privacy && typeof privacy.preferencesProcessingAllowed === 'function' && privacy.preferencesProcessingAllowed());
  }

  function recentStorageKey() {
    return 'gf-find-recent:' + (config.locale || '') + ':' + (config.country || '');
  }

  function recents() {
    if (!preferencesAllowed()) return [];
    try {
      var value = JSON.parse(localStorage.getItem(recentStorageKey()) || '[]');
      return Array.isArray(value)
        ? value.filter(function (query) { return typeof query === 'string' && query.trim(); }).slice(0, 5)
        : [];
    } catch (error) {
      return [];
    }
  }

  function pushRecent(query) {
    query = String(query || '').trim();
    if (!query || !preferencesAllowed()) return;
    try {
      var values = recents().filter(function (value) { return value !== query; });
      values.unshift(query);
      localStorage.setItem(recentStorageKey(), JSON.stringify(values.slice(0, 5)));
    } catch (error) {}
  }

  function setStatus(value) {
    if (footRight) footRight.textContent = value || '';
  }

  function setBusy(busy) {
    if (busy) {
      root.setAttribute('aria-busy', 'true');
      input.setAttribute('aria-busy', 'true');
    } else {
      root.removeAttribute('aria-busy');
      input.removeAttribute('aria-busy');
    }
  }

  function resultStatus(count) {
    return replaceToken(count === 1 ? strings.statusResultsOne : strings.statusResultsOther, 'count', count);
  }

  function renderHome() {
    var html = '';
    var recentQueries = recents();
    if (recentQueries.length) {
      html += '<div class="gf-find__label">' + esc(strings.recent) + '</div><div class="gf-find__chips">' +
        recentQueries.map(function (query) {
          return '<button type="button" class="gf-find__chip gf-find__chip--ghost" data-q="' + esc(query) + '">' + esc(query) + '</button>';
        }).join('') + '</div>';
    }
    html += '<div class="gf-find__label">' + esc(strings.try) + '</div><div class="gf-find__chips">' +
      (Array.isArray(strings.quick) ? strings.quick : []).map(quickChoice).filter(Boolean).map(function (choice) {
        return '<button type="button" class="gf-find__chip" data-q="' + esc(choice.query) + '">' + esc(choice.label) + '</button>';
      }).join('');
    if (config.catalogUrl) {
      html += '<button type="button" class="gf-find__chip gf-find__chip--ghost" data-go-catalog>' + esc(strings.browseEverything) + '</button>';
    }
    html += '</div>';
    body.innerHTML = html;
    setStatus(strings.statusIdle);
    afterRender();
  }

  function renderResults(query, results) {
    var products = results.products.slice(0, Number(config.resultLimit) || 8);
    var collections = results.collections.slice(0, 3);
    var html = '';
    if (collections.length) {
      html += '<div class="gf-find__label">' + esc(strings.collections) + '</div><div class="gf-find__colls">' +
        collections.map(function (collection) {
          var url = sameOriginStorefrontUrl(collection.url, location.origin, config.rootUrl);
          return url && collection.title
            ? '<a class="gf-find__coll" href="' + esc(url) + '" data-result-type="collection">⌘ ' + esc(collection.title) + '</a>'
            : '';
        }).join('') + '</div>';
    }
    if (products.length) {
      html += '<ul class="gf-find__list" role="listbox" id="gf-find-list">' +
        products.map(function (product, index) { return productRowHtml(product, index, query); }).join('') +
        '</ul>';
    }

    var count = products.length + collections.length;
    if (count) {
      html += '<div class="gf-find__chips" style="padding-top:8px">' +
        '<button type="button" class="gf-find__chip gf-find__chip--ghost" data-go-search>' +
          esc(replaceToken(strings.seeAllResults, 'query', query)) +
        '</button></div>';
    } else {
      html += '<div class="gf-find__empty"><span class="gf-find__empty-spark" aria-hidden="true">✦</span>' +
        '<p class="gf-find__empty-title">' + esc(replaceToken(strings.nothingMatches, 'query', query)) + '</p>' +
        '<p class="gf-find__empty-sub">' + esc(strings.emptyHelp) + '</p></div>' +
        '<div class="gf-find__chips" style="justify-content:center">' +
          '<button type="button" class="gf-find__chip gf-find__chip--ghost" data-go-catalog>' + esc(strings.browseEverything) + '</button>' +
        '</div>';
    }

    body.innerHTML = html;
    setStatus(resultStatus(count));
    afterRender();
    track('view_search_results', {
      search_term: query,
      gf_product_results: products.length,
      gf_collection_results: collections.length,
      currency: config.currency,
      country: config.country
    });
  }

  function renderError(query) {
    body.innerHTML = '<div class="gf-find__empty"><span class="gf-find__empty-spark" aria-hidden="true">✦</span>' +
      '<p class="gf-find__empty-title">' + esc(strings.statusUnavailable) + '</p></div>' +
      '<div class="gf-find__chips" style="justify-content:center">' +
        '<button type="button" class="gf-find__chip" data-gf-find-retry>' + esc(strings.retry) + '</button>' +
        '<button type="button" class="gf-find__chip gf-find__chip--ghost" data-go-catalog>' + esc(strings.browseEverything) + '</button>' +
      '</div>';
    setStatus(strings.statusUnavailable);
    afterRender();
    var retry = body.querySelector('[data-gf-find-retry]');
    if (retry) retry.addEventListener('click', function () { performSearch(query); });
  }

  function track(eventName, parameters) {
    if (typeof window.gtag !== 'function') return;
    var output = parameters || {};
    output.gf_language = config.locale;
    output.gf_country = config.country;
    output.gf_currency = config.currency;
    try { window.gtag('event', eventName, output); } catch (error) {}
  }

  function recordSearch(resultType) {
    if (!currentQuery) return;
    pushRecent(currentQuery);
    track('search', {
      search_term: currentQuery,
      gf_result_type: resultType || 'all',
      currency: config.currency,
      country: config.country
    });
  }

  function afterRender() {
    rows = Array.prototype.slice.call(body.querySelectorAll('.gf-find__row'));
    selected = 0;
    if (rows[0]) input.setAttribute('aria-activedescendant', rows[0].id);
    else input.removeAttribute('aria-activedescendant');

    rows.forEach(function (row, index) {
      row.addEventListener('mousemove', function () { select(index); });
      row.addEventListener('click', function () { recordSearch('product'); });
    });
    body.querySelectorAll('.gf-find__coll').forEach(function (link) {
      link.addEventListener('click', function () { recordSearch('collection'); });
    });
    body.querySelectorAll('[data-q]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        input.value = chip.getAttribute('data-q') || '';
        input.focus();
        onInput();
      });
    });
    body.querySelectorAll('[data-go-search]').forEach(function (button) {
      button.addEventListener('click', gotoSearchPage);
    });
    body.querySelectorAll('[data-go-catalog]').forEach(function (button) {
      button.addEventListener('click', gotoCatalog);
    });
  }

  function select(index) {
    if (!rows.length) return;
    selected = (index + rows.length) % rows.length;
    rows.forEach(function (row, rowIndex) {
      row.setAttribute('aria-selected', rowIndex === selected ? 'true' : 'false');
    });
    input.setAttribute('aria-activedescendant', rows[selected].id || '');
    rows[selected].scrollIntoView({ block: 'nearest' });
  }

  function gotoSearchPage() {
    if (!currentQuery) return;
    recordSearch('all');
    var url = new URL(config.searchUrl, location.origin);
    url.searchParams.set('q', currentQuery);
    location.href = url.toString();
  }

  function gotoCatalog() {
    var url = sameOriginStorefrontUrl(config.catalogUrl, location.origin, config.rootUrl);
    if (url) location.href = url;
  }

  function abortRequest() {
    if (requestController) requestController.abort();
    requestController = null;
  }

  function performSearch(query) {
    var key = cacheKey(query);
    if (Object.prototype.hasOwnProperty.call(responseCache, key)) {
      setBusy(false);
      renderResults(query, responseCache[key]);
      return;
    }

    abortRequest();
    var serial = ++requestSerial;
    requestController = typeof window.AbortController === 'function' ? new window.AbortController() : null;
    setBusy(true);
    setStatus(strings.statusLoading);

    var url;
    try {
      url = predictiveJsonUrl(config.predictiveSearchUrl, query, Number(config.resultLimit) || 8, location.origin);
    } catch (error) {
      setBusy(false);
      renderError(query);
      return;
    }

    var options = {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    };
    if (requestController) options.signal = requestController.signal;

    fetch(url, options).then(function (response) {
      if (!response.ok) throw new Error('Predictive search HTTP ' + response.status);
      return response.json();
    }).then(function (payload) {
      if (serial !== requestSerial || currentQuery !== query) return;
      var results = resultSets(payload);
      cacheResponse(key, results);
      requestController = null;
      setBusy(false);
      renderResults(query, results);
    }).catch(function (error) {
      if (serial !== requestSerial || currentQuery !== query || (error && error.name === 'AbortError')) return;
      requestController = null;
      setBusy(false);
      renderError(query);
    });
  }

  function onInput() {
    currentQuery = input.value.trim();
    root.classList.toggle('gf-find--active', !!currentQuery);
    clearTimeout(debounceTimer);
    abortRequest();
    requestSerial += 1;
    setBusy(false);
    rows = [];
    input.removeAttribute('aria-activedescendant');

    if (!currentQuery) {
      renderHome();
      return;
    }

    body.innerHTML = '';
    setStatus(strings.statusLoading);
    debounceTimer = setTimeout(function () {
      performSearch(currentQuery);
    }, Number(config.debounceMs) || 160);
  }

  function open() {
    if (root.hasAttribute('data-open')) return;
    previousFocus = document.activeElement;
    root.setAttribute('data-open', '');
    root.setAttribute('aria-hidden', 'false');
    input.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
    input.value = '';
    input.placeholder = strings.placeholder || '';
    currentQuery = '';
    root.classList.remove('gf-find--active');
    renderHome();
    setTimeout(function () { input.focus(); }, 60);

    var placeholders = Array.isArray(strings.placeholders) ? strings.placeholders.filter(Boolean) : [];
    if (placeholders.length && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      placeholderIndex = 0;
      clearInterval(placeholderTimer);
      placeholderTimer = setInterval(function () {
        if (input.value) return;
        placeholderIndex = (placeholderIndex + 1) % placeholders.length;
        input.placeholder = placeholders[placeholderIndex];
      }, 2600);
    }
  }

  function close() {
    if (!root.hasAttribute('data-open')) return;
    clearTimeout(debounceTimer);
    clearInterval(placeholderTimer);
    abortRequest();
    requestSerial += 1;
    setBusy(false);
    root.removeAttribute('data-open');
    root.setAttribute('aria-hidden', 'true');
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    input.placeholder = strings.placeholder || '';
    document.documentElement.style.overflow = '';
    if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
    previousFocus = null;
  }

  function trapFocus(event) {
    if (event.key !== 'Tab' || !root.hasAttribute('data-open')) return;
    var focusable = Array.prototype.slice.call(root.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (element) { return element.offsetParent !== null; });
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  input.addEventListener('input', onInput);
  input.addEventListener('keydown', function (event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      select(selected + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      select(selected - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (rows[selected]) {
        recordSearch('product');
        location.href = rows[selected].getAttribute('href');
      } else {
        gotoSearchPage();
      }
    }
  });

  form.addEventListener('submit', function (event) {
    event.preventDefault();
    currentQuery = input.value.trim();
    gotoSearchPage();
  });

  root.querySelector('.gf-find__veil').addEventListener('click', close);
  root.querySelectorAll('[data-gf-find-close]').forEach(function (button) {
    button.addEventListener('click', close);
  });

  document.addEventListener('keydown', function (event) {
    trapFocus(event);
    if (event.key === 'Escape' && root.hasAttribute('data-open')) {
      event.preventDefault();
      close();
      return;
    }
    if ((event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K')) {
      event.preventDefault();
      open();
      return;
    }
    if (event.key === '/' && !root.hasAttribute('data-open')) {
      var target = event.target;
      var typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (!typing) {
        event.preventDefault();
        open();
      }
    }
  });

  /* Real locale-aware search links remain intact until this enhancement has
     initialized, and modified clicks continue to use the normal link. */
  function claimTriggers() {
    var selector = '[data-gf-search-open], [on\\:click="#search-modal/showDialog"]';
    document.querySelectorAll(selector).forEach(function (trigger) {
      if (trigger.__gfClaimed) return;
      trigger.__gfClaimed = true;
      trigger.removeAttribute('on:click');
      trigger.addEventListener('click', function (event) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault();
        event.stopPropagation();
        open();
      });
    });
  }

  claimTriggers();
  document.addEventListener('shopify:section:load', claimTriggers);
})();
